import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user, token, isAuthenticated } = useAuth();
  
  // Use refs to avoid dependency issues
  const socketRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 10;

  // Show browser notification
  const showBrowserNotification = useCallback((notificationData) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification(notificationData.title, {
        body: notificationData.message,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: notificationData.id,
        requireInteraction: notificationData.is_important,
        silent: false
      });

      notification.onclick = () => {
        window.focus();
        if (notificationData.action_url) {
          window.location.href = notificationData.action_url;
        }
        notification.close();
      };

      // Auto close after 5 seconds for non-important notifications
      if (!notificationData.is_important) {
        setTimeout(() => notification.close(), 5000);
      }
    }
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    try {
      // Create a simple notification sound
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch (error) {
      console.log('Could not play notification sound:', error);
    }
  }, []);

  // Disconnect socket
  const disconnectSocket = useCallback(() => {
    if (socketRef.current) {
      console.log('🔌 Disconnecting WebSocket...');
      
      // Remove all listeners before disconnecting
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
      
      setSocket(null);
      setIsConnected(false);
      setConnectionError(null);
      reconnectAttemptsRef.current = 0;
    }
  }, []);

  // Initialize socket connection
  useEffect(() => {
    if (!isAuthenticated || !token) {
      disconnectSocket();
      return;
    }

    // Don't reconnect if already connected
    if (socketRef.current?.connected) {
      return;
    }

    console.log('🔌 Connecting to WebSocket server...');

    const serverUrl = process.env.REACT_APP_SERVER_URL || '/api';
    
    const newSocket = io(serverUrl, {
      auth: {
        token: token
      },
      transports: ['websocket', 'polling'],
      timeout: 20000,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: maxReconnectAttempts
    });

    // Connection events
    newSocket.on('connect', () => {
      console.log('✅ Connected to WebSocket server');
      setIsConnected(true);
      setConnectionError(null);
      reconnectAttemptsRef.current = 0;
    });

    newSocket.on('connected', (data) => {
      console.log('🎉 WebSocket authentication successful:', data.message);
    });

    newSocket.on('disconnect', (reason) => {
      console.log('🔌 Disconnected from WebSocket server:', reason);
      setIsConnected(false);
      
      if (reason === 'io server disconnect') {
        // Server disconnected us, try to reconnect
        reconnectAttemptsRef.current++;
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          console.log(`Attempting to reconnect (${reconnectAttemptsRef.current}/${maxReconnectAttempts})...`);
        }
      }
    });

    newSocket.on('connect_error', (error) => {
      console.error('❌ WebSocket connection error:', error.message);
      setConnectionError(error.message);
      setIsConnected(false);
      reconnectAttemptsRef.current++;
      
      if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
        console.error('Max reconnection attempts reached. Giving up.');
        newSocket.disconnect();
      }
    });

    // Notification events
    newSocket.on('notification:new', (notificationData) => {
      console.log('📥 New notification received:', notificationData);
      
      // Add to notifications list
      setNotifications(prev => [notificationData, ...prev]);
      
      // Update unread count
      setUnreadCount(prev => prev + 1);
      
      // Show browser notification if permission granted
      showBrowserNotification(notificationData);
      
      // Play notification sound (optional)
      playNotificationSound();
    });

    newSocket.on('notification:read', (data) => {
      console.log('📖 Notification marked as read:', data.notificationId);
      
      // Update local state
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === data.notificationId 
            ? { ...notif, is_read: true, read_at: new Date().toISOString() }
            : notif
        )
      );
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    });

    newSocket.on('notification:deleted', (data) => {
      console.log('🗑️ Notification deleted:', data.notificationId);
      
      // Remove from local state
      setNotifications(prev => 
        prev.filter(notif => notif.id !== data.notificationId)
      );
    });

    // Ping/Pong for connection health
    newSocket.on('pong', (data) => {
      console.log('🏓 Pong received:', data.timestamp);
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    // Cleanup function
    return () => {
      if (socketRef.current) {
        console.log('🧹 Cleaning up socket connection...');
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [isAuthenticated, token, showBrowserNotification, playNotificationSound, disconnectSocket]);

  // Socket utility functions
  const emitEvent = useCallback((event, data) => {
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit(event, data);
      return true;
    }
    console.warn('Cannot emit event: Socket not connected');
    return false;
  }, []);

  const markNotificationAsRead = useCallback((notificationId) => {
    return emitEvent('notification:read', { notificationId });
  }, [emitEvent]);

  const deleteNotification = useCallback((notificationId) => {
    return emitEvent('notification:delete', { notificationId });
  }, [emitEvent]);

  const sendPing = useCallback(() => {
    return emitEvent('ping');
  }, [emitEvent]);

  // Request browser notification permission
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window) {
      try {
        const permission = await Notification.requestPermission();
        console.log('Browser notification permission:', permission);
        return permission === 'granted';
      } catch (error) {
        console.error('Error requesting notification permission:', error);
        return false;
      }
    }
    return false;
  }, []);

  // Auto-request notification permission on first connection
  useEffect(() => {
    if (isConnected && 'Notification' in window && Notification.permission === 'default') {
      requestNotificationPermission();
    }
  }, [isConnected, requestNotificationPermission]);

  const value = {
    socket,
    isConnected,
    connectionError,
    notifications,
    unreadCount,
    
    // Actions
    disconnectSocket,
    emitEvent,
    markNotificationAsRead,
    deleteNotification,
    sendPing,
    requestNotificationPermission,
    
    // State setters (for manual updates)
    setNotifications,
    setUnreadCount
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;
